import React, { useCallback, useMemo, useRef, useState, useImperativeHandle, forwardRef } from "react";
import { RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCHTMLTextEditor, RCCTooltip, RCCButton, RCCServicesApi } from "react-common-components";
import { useFieldArray, useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import DeleteModal from "src/components/DeleteModal";
import RACI from "src/components/RACI";
import Tabs from "src/components/Tabs";
import Title from "src/components/Title";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import LovTags from "src/components/Lov/Tags";
import extractSingleValueFromArrayOfObject from "src/utils/extractSingleValueFromArrayOfObject";
import convertBooleanValueToCharInArray from "src/utils/convertBooleanValueToCharInArray";
import Audit from "src/components/Audit";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import MetricsRanges from "src/components/MetricsRanges";
import { PermissionEnum } from "src/context/Auth/interface";
import CheckPermission from "src/components/CheckPermission";
import { useAuth } from "src/context/Auth/context";
import stringToNumber from "src/utils/stringToNumber";
import { addOrChangeSchema, deleteSchema } from "./schemas";
import ListKR from "../components/ListKR";
import { IKeyResults } from "../components/ListKR/interface";
import IOrgChartObjectivesRef, { IObjective, IOrgChartProps, TabsOKRMODAL } from "./interface";
import FormSimplifiedFields from "./formSimplifiedFields";

const InitialMetricRanges = [
	{oka002Cor:"#D91E18", oka002Valorini:0, oka002Valorfim:80.99},
	{oka002Cor:"#F5AB35", oka002Valorini:81, oka002Valorfim: 99.99 },
	{oka002Cor:"#019875", oka002Valorini: 100, oka002Valorfim:9999 }
];

const ObjectivesModal: React.ForwardRefRenderFunction<IOrgChartObjectivesRef, IOrgChartProps> = ({
	openModalAddOrChangeKr,
	openModalDeleteKr,
	openModalPerformCheckIn,
	openModalOKRComments,
	openModalOKRAttachments
}, ref) => {
	const { translate, currentLanguage } = useTranslation();
	const { user } = useAuth();
	const { setLoading } = useLoaderBackdrop();
	const {addToast} = RCCHookToast();

	// Permissões
	const { checkIfHasSpecificPermission, administrator, superUser } = useAuth();
	const changePermission = checkIfHasSpecificPermission("okr/panel", PermissionEnum.CREATE_UPDATE);

	// Modal Add/Change Objective
	const [tabInit, setTabInit] = useState(0);
	const onCallbackAddOrChange = useRef<Function | undefined>(undefined);
	const [selectedObjective, setSelectedObjective] = useState<IObjective>({} as IObjective);
	const [listKRs, setListKRs] = useState<IKeyResults[]>([]);
	const [isAdd, setIsAdd] = useState(true);
	const [simpleForm, setSimpleForm] = useState(false);
	const modalAddOrChange = useRef<RCCRefModalProps>(null);
	const [disabled, setDisabled] = useState(false);

	const { 
		handleSubmit : handleSubmitAddOrChange, register : registerAddOrChange, control: controlAddOrChange ,
		reset : resetAddOrChange, setError : setErrorAddOrChange, formState: { errors : errorsAddOrChange }, clearErrors: clearErrorsAddOrChange,
		unregister: unregisterAddOrChange, setValue: setValueAddOrChange, watch: watchAddOrChange
	} = useForm();

	const { 
		fields: fieldsMetricRanges, 
		remove: removeMetricRanges,
		append: appendMetricRanges
	} = useFieldArray({
		control: controlAddOrChange,
		name: "oktba002"
	});

	const openModalAddOrChange = async (id?: number, okrParentId?: number, onCallback?: Function, tabInitSelected?: TabsOKRMODAL | undefined) => {

		resetAddOrChange();
		if(id){
			setIsAdd(false);
			setSimpleForm(false);
			try{
				setLoading(true);
				const {data} = await RCCServicesApi.getRequest({
					method: "OKR/Select",
					urlParameters: {
						id
					}
				});

				if((changePermission && data?.permissions > 1) || administrator || superUser)
					setDisabled(false);
				else
					setDisabled(true);

				setSelectedObjective(data);
				setListKRs(data.oktbb001001);
				if(!data.oktba002 || (data.oktba002 && data.oktba002.length === 0))
					appendMetricRanges(InitialMetricRanges);
				else
					appendMetricRanges(data.oktba002);
					
				setLoading(false);
			}catch(err){
				setLoading(false);
				standardErrorMessages(err, translate("Erro ao selecionar OKR"), addToast);
			}
		}else{
			setIsAdd(true);
			setSimpleForm(true);
			setDisabled(false);
			const objective = {
				oka001Efa005Id: user.efa005Id,
				oka001Efa005Id2: user.efa005Id
			} as IObjective;

			if(okrParentId)
				objective.oka001Oka001Id = okrParentId;

			setSelectedObjective(objective);

			appendMetricRanges(InitialMetricRanges);
		}
		onCallbackAddOrChange.current = onCallback;
		setTabInit(tabInitSelected || 0);
		modalAddOrChange.current?.openModal();
	};
	
	const submitAddOrChange = async (data: any) => {	
		const dataForm = data;
		if(data.oka001Tags)
			dataForm.oka001Tags = extractSingleValueFromArrayOfObject("value", data.oka001Tags);
		else
			dataForm.oka001Tags = undefined;
		try{
			clearErrorsAddOrChange();
			modalAddOrChange.current?.setLoadingButton();
			
			if(data.oktba006 && data.oktba006.length > 0)
				dataForm.oktba006 = dataForm.oktba006.map((oka006: any) => {
					if(typeof oka006.oka006Efa005Id === "object")
						return {
							...oka006,
							oka006Efa005Id: oka006.oka006Efa005Id.value
						};
					if(typeof oka006.oka006Efa004Id === "object")
						return {
							...oka006,
							oka006Efa004Id: oka006.oka006Efa004Id.value
						};
					return oka006;
				});

			if(typeof data.oka001Efa005Id === "object")
				dataForm.oka001Efa005Id = data.oka001Efa005Id.value;
			
			if(typeof data.oka001Efa005Id2 === "object")
				dataForm.oka001Efa005Id2 = data.oka001Efa005Id2.value;

			dataForm.oka001Taa006Id = Number(data.oka001Taa006Id) === 0 ? undefined : Number(data.oka001Taa006Id);

			if(data.oktba002)
				dataForm.oktba002 = data.oktba002.map((metric: any) => ({
					...metric,
					oka002Valorfim: stringToNumber(metric.oka002Valorfim, currentLanguage),
					oka002Valorini: stringToNumber(metric.oka002Valorini, currentLanguage)
				}));

			await addOrChangeSchema(translate, currentLanguage).validate({ ...dataForm }, {
				abortEarly: false
			});
				
			if(data.oktba006 && data.oktba006.length > 0){
				dataForm.oktba006 = data.oktba006.filter((dt: any) => dt.oka006Efa005Id !== 0);
				dataForm.oktba006 = data.oktba006.filter((dt: any) => dt.oka006Efa004Id !== 0);
				dataForm.oktba006 = convertBooleanValueToCharInArray("oka006Notificaparticipante", data.oktba006);
			}

			if(isAdd){
				if(selectedObjective.oka001Oka001Id)
					dataForm.oka001Oka001Id = selectedObjective.oka001Oka001Id;

				await RCCServicesApi.postRequest({
					method: "OKR/Insert",
					body: {
						...dataForm,
						oka001Notificaresponsavel: dataForm.oka001Notificaresponsavel ? "S" : "N",
						oka001Notificaautoridade: dataForm.oka001Notificaautoridade ? "S" : "N"
					}
				});
			}else{				
				await RCCServicesApi.putRequest({
					method: "OKR/Edit",
					body: {
						...dataForm,
						oka001Id: selectedObjective.oka001Id,
						oka001Notificaresponsavel: dataForm.oka001Notificaresponsavel ? "S" : "N",
						oka001Notificaautoridade: dataForm.oka001Notificaautoridade ? "S" : "N"
					}
				});
			}

			addToast({
				color: "green",
				description: isAdd ? translate("OKR adicionado com sucesso") : translate("OKR alterado com sucesso"),
				title: translate("Sucesso")
			});

			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();

			if(onCallbackAddOrChange.current !== undefined)
				onCallbackAddOrChange.current();
			
		}catch(err){
			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAddOrChange)){
				addToast({
					title: translate("Erro"),
					description: translate("Verifique os erros no formulário e tente novamente"),
					color: "yellow"
				});
				return;
			}

			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar OKR") : translate("Erro ao alterar OKR"), addToast);
		}
	};

	// Modal Delete Objective
	const onCallbackDelete = useRef<Function | undefined>(undefined);
	const modalDelete = useRef<RCCRefModalProps>(null); 
	const { handleSubmit: handleSubmitDelete, register: registerDelete, reset : resetDelete, setError : setErrorDelete, formState: { errors : errorsDelete }, clearErrors: clearErrorsDelete } = useForm();
	const openModalDelete = (id: number, title: string, onCallback?: Function) => { 
		resetDelete();
		const objective = {} as IObjective;
		objective.oka001Id = id;
		objective.oka001Titulo = title;
		setSelectedObjective(objective); 
		onCallbackDelete.current = onCallback;
		modalDelete.current?.openModal();
	};
	const submitDelete = async (data: any) => {
		try{
			clearErrorsDelete();	
			modalDelete.current?.setLoadingButton();

			await deleteSchema(translate).validate({...data, "oka001TituloConfirmation": selectedObjective.oka001Titulo},{
				abortEarly: false
			});
			
			await RCCServicesApi.deleteRequest({
				method: "OKR/Delete",
				urlParameters: {
					id: selectedObjective.oka001Id,
					title: selectedObjective.oka001Titulo
				}
			});

			addToast({
				color: "green",
				description: translate("OKR excluído com sucesso"),
				title: translate("Sucesso")
			});
			modalDelete.current?.resetLoadingButton();
			modalDelete.current?.closeModal();

			if(onCallbackDelete.current !== undefined)
				onCallbackDelete.current();

		}catch(err){
			modalDelete.current?.resetLoadingButton();

			if(getValidationErrors(err, setErrorDelete))
				return;

			standardErrorMessages(err, translate("Erro ao excluir OKR"), addToast);
		}
		
	};


	const callbackOnAddKr = useCallback(async (id: number) => {
		if(!id)
			return;

		try{
			const {data} = await RCCServicesApi.getRequest({
				method: "KR/InfosOfKRForList",
				urlParameters: {
					id
				}
			});	

			setListKRs((prevState) => [data, ...prevState]);
		}catch(err){
			standardErrorMessages(err, translate("Erro ao atualizar Resultado Chave, favor atualizar a pagina"), addToast);
		}
		
	}, []);

	const callbackOnUpdateKr = useCallback(async (id: number) => {
		try{
			const {data} = await RCCServicesApi.getRequest({
				method: "KR/InfosOfKRForList",
				urlParameters: {
					id
				}
			});
			setListKRs((prevState) => prevState.map((kr) => {
				if(kr.okb001Id === id)
					return {
						...data,
						colorGraph: data.colorGraph === null ? undefined : data.colorGraph
					};
	
				return kr;
			}));

		}catch(err){
			standardErrorMessages(err, translate("Erro ao atualizar Resultado Chave, favor atualizar a pagina"), addToast);
		}
	}, []);

	const callbackOnDeleteKr = useCallback((id: number) => {
		setListKRs((prevState) => prevState.filter((kr) => kr.okb001Id !== id));
	}, []);

	const callbackComments = async (id: number) => {
		if(!id)
			return;

		try{
			const {data} = await RCCServicesApi.getRequest({
				method: "OKR/CommentsNumber",
				urlParameters: {
					id
				}
			});	

			setSelectedObjective({...selectedObjective, commentsNumber: data});
		}catch(err){
			standardErrorMessages(err, translate("Erro ao atualizar número de comentários, favor atualizar a pagina"), addToast);
		}
	};

	const callbackAttachments = async (id: number) => {
		if(!id)
			return;

		try{
			const {data} = await RCCServicesApi.getRequest({
				method: "OKR/AttachmentsNumber",
				urlParameters: {
					id
				}
			});	

			setSelectedObjective({...selectedObjective, attachmentsNumber: data});
		}catch(err){
			standardErrorMessages(err, translate("Erro ao atualizar número de anexos, favor atualizar a pagina"), addToast);
		}
	};

	const ListOfKR = useMemo(() => (
		<ListKR 
			data={listKRs}
			callbackOnDelete={callbackOnDeleteKr}
			callbackOnUpdate={callbackOnUpdateKr}
			openModalAddOrChangeKr={openModalAddOrChangeKr}
			openModalDeleteKr={openModalDeleteKr}
			openModalPerformCheckIn={openModalPerformCheckIn}	
			disabled={disabled}
		/>
	), [listKRs, disabled]);

	const MetricsRangesMemo = useMemo(() => (
		<>
			<MetricsRanges
				names ={{
					table:"oktba002",
					idName: "oka002Id",
					initValueName:"oka002Valorini",
					endValueName:"oka002Valorfim",
					colorName:"oka002Cor",
					metricIdName:"oka001Taa006Id"
				}}
				source={fieldsMetricRanges}
				control={controlAddOrChange}
				remove={removeMetricRanges}
				append={appendMetricRanges}
				errors={errorsAddOrChange}
				clearErrors={clearErrorsAddOrChange}
				setValue={setValueAddOrChange}
				metricId={selectedObjective.oka001Taa006Id?.toString()}
				useMetricLov
				disabled={disabled}
			/>
		</>
	), [selectedObjective, errorsAddOrChange, fieldsMetricRanges, disabled]);

	useImperativeHandle(ref, () => ({
		openModalDelete,
		openModalAddOrChange
	}));

	return (
		<>
			{/* Modal Delete */}
			<DeleteModal
				ref={modalDelete}
				title= {translate("Exclusão de OKR")}
				description= {`${translate("Deseja realmente excluir o OKR")}: ${selectedObjective.oka001Titulo}?`}
				onSubmit = {handleSubmitDelete(submitDelete)} 
				height="250px"
			>
				<RCCGrid
					container
					item
					xs={10}
				>
					<RCCInput 
						register={registerDelete}
						name="oka001Titulo"
						label={translate("Digite o título do OKR para excluí-lo")}
						maxLength={200}
						error={errorsDelete.oka001Titulo}
						required
					/>
				</RCCGrid>
			</DeleteModal>
		
			{/* Modal Add/Change */}
			<Modal
				ref={modalAddOrChange}
				header={{
					title: isAdd ? translate("Adicionar OKR") : translate("Alterar OKR"),
					subtitle: !isAdd ? `OKR: ${selectedObjective.oka001Codigo ? `${selectedObjective.oka001Codigo} - ` : ""}${selectedObjective.oka001Titulo}` : undefined,
					icon: {
						name: isAdd ? "plus" : "pencil"
					}
				}}
				footer={{
					showSaveButton: !disabled,
					buttonLoadingText: translate("Carregando"),
					saveButtonText: translate("Salvar"),
					cancelButtonText: translate("Cancelar"),
					childrenOfStandardFooter: <>
						{isAdd &&
							<RCCButton type="button" color="blue" 
								size="sm"
								onClick={() => { 
									const oka001Codigo = watchAddOrChange("oka001Codigo");
									let oka001Efa005Id = watchAddOrChange("oka001Efa005Id");
	
									if(typeof oka001Efa005Id === "object")
										oka001Efa005Id = oka001Efa005Id.value;
	
									let oka001Efa005Id2 = watchAddOrChange("oka001Efa005Id2");
	
									if(typeof oka001Efa005Id2 === "object")
										oka001Efa005Id2 = oka001Efa005Id2.value;
	
									const oka001Titulo = watchAddOrChange("oka001Titulo");
									const oka001Dtinicio = watchAddOrChange("oka001Dtinicio");
									const oka001Dtfim = watchAddOrChange("oka001Dtfim");
									const oka001Nivel = watchAddOrChange("oka001Nivel");
									const oka001Taa001Id = watchAddOrChange("oka001Taa001Id");
									const oka001Periodo = watchAddOrChange("oka001Periodo");

									setSelectedObjective((prevState) => ({
										...prevState,
										oka001Efa005Id,
										oka001Efa005Id2,
										oka001Taa001Id,
										oka001Dtinicio,
										oka001Dtfim,
										oka001Nivel,
										oka001Titulo,
										oka001Codigo,
										oka001Periodo
									}));

									setSimpleForm((prevState) => !prevState); 
								}}
							>
								{translate(`Ver cadastro ${simpleForm ? "completo" :"simplificado"}`)}
							</RCCButton>
						}
					</>
				}}
				onSubmit={handleSubmitAddOrChange(submitAddOrChange)}
				onClose={() => {
					if(!isAdd && onCallbackAddOrChange.current !== undefined)
						onCallbackAddOrChange.current();
				}}
				withoutForm
			>
				{simpleForm ? (
					<RCCGrid container item direction="column" alignItems="center">
						<FormSimplifiedFields 
							controlAddOrChange={controlAddOrChange}
							errorsAddOrChange={errorsAddOrChange}
							isAdd={isAdd}
							registerAddOrChange={registerAddOrChange}
							selectedObjective={selectedObjective}
							setValueAddOrChange={setValueAddOrChange}
							simpleForm
							disabled={disabled}
						/>
					</RCCGrid>
				): (
					<Tabs 
						name="TabOKR"
						tabInit={tabInit}
						options={[{
							label: translate("Geral"),
							content: (
								<RCCGrid
									container
									item
									direction="column"
									alignItems="center"
								>
									{!isAdd && (
										<RCCGrid
											container
											item
											xs={12}
											sm={10}
											md={8}
											lg={6}
											justifyContent="flex-end"
										>
											<RCCGrid item>
												<RCCTooltip color="grey" text={translate("Comentários")}>
													<RCCButton color="grey" icon={{ name: selectedObjective.commentsNumber > 0 ? "commentFilled" : "comment", size: 12 }} onClick={() => {
														if(!openModalOKRComments)
															return;

														openModalOKRComments(selectedObjective.oka001Id, selectedObjective.oka001Titulo, () => callbackComments(selectedObjective.oka001Id), disabled);
													}}>
														{selectedObjective.commentsNumber ? selectedObjective.commentsNumber.toString() : "0"}
													</RCCButton>
												</RCCTooltip>
											</RCCGrid>
											<RCCGrid item>
												<RCCTooltip color="grey" text={translate("Anexos")}>
													<RCCButton color="grey" icon={{ name: "attachment", size: 12 }} onClick={() => {
														if(!openModalOKRAttachments)
															return;
															
														openModalOKRAttachments(selectedObjective.oka001Id, selectedObjective.oka001Titulo, () => callbackAttachments(selectedObjective.oka001Id), disabled);
													}}>
														{selectedObjective.attachmentsNumber ? selectedObjective.attachmentsNumber.toString() : "0"}
													</RCCButton>
												</RCCTooltip>
											</RCCGrid>
											<RCCGrid item>
												<Audit data={selectedObjective} type="pop" />
											</RCCGrid>
										</RCCGrid>
									)}
									<FormSimplifiedFields 
										controlAddOrChange={controlAddOrChange}
										errorsAddOrChange={errorsAddOrChange}
										isAdd={isAdd}
										registerAddOrChange={registerAddOrChange}
										selectedObjective={selectedObjective}
										setValueAddOrChange={setValueAddOrChange}
										simpleForm={false}
										disabled={disabled}
									/>
									<RCCGrid
										container
										item
										xs={12}
										sm={10}
										md={8}
										lg={6}
									>
										<LovTags 
											control={controlAddOrChange}
											name="oka001Tags"
											label={translate("Tags")}
											defaultValue={Array.isArray(selectedObjective.oka001Tags) ? selectedObjective.oka001Tags.join(", ") : selectedObjective.oka001Tags}
											extraParametersOnLoad={{
												use: 1
											}}
											extraParametersOnCreate={{
												use: 1
											}}
											disabled={disabled}
										/>
									</RCCGrid>
									<RCCGrid
										container
										item
										xs={12}
										sm={10}
										md={8}
										lg={6}
									>
										<RCCHTMLTextEditor
											name="oka001Missao"
											label={translate("Missão")}
											control={controlAddOrChange}
											defaultValue={selectedObjective.oka001Missao || ""}
											disabled={disabled}
										/>
									</RCCGrid>
									<RCCGrid
										container
										item
										xs={12}
										sm={10}
										md={8}
										lg={6}
									>
										<RCCHTMLTextEditor
											name="oka001Premissas"
											label={translate("Premissas")}
											control={controlAddOrChange}
											defaultValue={selectedObjective.oka001Premissas || ""}
											disabled={disabled}
										/>
									</RCCGrid>
								</RCCGrid>
							)
						},{
							label: translate("RACI"),
							content: (
								<RACI 
									accountable={{
										nameOfSelect: "oka001Efa005Id2",
										nameOfCheckbox: "oka001Notificaautoridade",
										defaultValue: {
											select: selectedObjective.oka001Efa005Id2 ? selectedObjective.oka001Efa005Id2.toString() : "",
											checkbox: selectedObjective.oka001Notificaautoridade
										}
									}}
									control={controlAddOrChange}
									register={registerAddOrChange}
									unregister={unregisterAddOrChange}
									clearErrors={clearErrorsAddOrChange}
									error={errorsAddOrChange}
									participants={{
										nameOfObject: "oktba006",
										nameOfID: "oka006Id",
										nameOfCheckbox: "oka006Notificaparticipante",
										nameOfSelectGroup: "oka006Efa004Id",
										nameOfSelectGroupDescription: "oka006Efa004Nome",
										nameOfSelectUser: "oka006Efa005Id",
										nameOfSelectUserDescription: "oka006Efa005Nome",
										nameOfType: "oka006Tipo",
										defaultValue: selectedObjective.oktba006
									}}
									responsible={{
										nameOfSelect: "oka001Efa005Id",
										nameOfCheckbox: "oka001Notificaresponsavel",
										defaultValue: {
											select: selectedObjective.oka001Efa005Id ? selectedObjective.oka001Efa005Id.toString() : "",
											checkbox: selectedObjective.oka001Notificaresponsavel
										}
									}}
									disabled={disabled}
								/>
							)
						},{
							label: translate("Métricas"),
							content: (
								<RCCGrid
									container
									item
									justifyContent="center"
									alignItems="center"
								>
									<RCCGrid
										container
										item
										xs={12}
										sm={10}
										md={8}
										lg={6}
									>
										{MetricsRangesMemo}
									</RCCGrid>
								</RCCGrid>
							)
						},{
							label: translate("Resultados Chave"),
							hidden: isAdd,
							content: (
								<RCCGrid
									container
									item
									direction="column"
								>
									<RCCGrid
										container
										item
										justifyContent="space-between"
										alignItems="center"
									>
										<Title
											color="grey"
											size="sm"
										>
											{translate("Os resultados chave são utilizados para preencher os percentuais no okr")}
										</Title>
										<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
											<RCCTooltip
												color="purple"
												text={translate("Adicionar Resultado Chave")}
											>
												<RCCButton
													color="purple"
													icon={{
														name: "plus"
													}}
													layout="isRounded"
													size="md"
													onClick={() => openModalAddOrChangeKr && openModalAddOrChangeKr(undefined, selectedObjective.oka001Id, selectedObjective.oka001Titulo, callbackOnAddKr)}
													disabled={disabled}
												/>
											</RCCTooltip>
										</CheckPermission>
									</RCCGrid>

									{ListOfKR}
									

								</RCCGrid>
							)
						}]}
					/>
				)}
			</Modal>
		</>
	);
};

export default forwardRef(ObjectivesModal);
